// Final tier. Replaces the inline Calendly iframe (which was a fixed-size
// scroll-trapped panel) with the Calendly popup widget. Click the button
// → Calendly opens as a full-window overlay → user books → overlay closes.
// Stays on-page, no scroll-in-iframe, professional feel.
//
// Calendly script and CSS lazy-load on mount so the bundle doesn't carry
// them for users who never reach this section.

import { useEffect } from 'react';
import { Calendar, ExternalLink } from 'lucide-react';
import type { SelectedLens } from '../lib/api';
import { track } from '../lib/mixpanel';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (opts: { url: string }) => void;
      closePopupWidget: () => void;
    };
  }
}

interface Props {
  selectedLenses: SelectedLens[];
}

const CALENDLY_URL =
  (import.meta.env.VITE_CALENDLY_URL as string | undefined) ??
  'https://calendly.com/sagentivum';

const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_STYLE_URL = 'https://assets.calendly.com/assets/external/widget.css';

export default function MeetingGate({ selectedLenses }: Props) {
  const lensNames = selectedLenses.map((l) => l.name);

  useEffect(() => {
    track('meeting_gate_viewed', { lens_names: lensNames }); // event 9
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy-load Calendly's script + CSS once on mount.
  useEffect(() => {
    if (!document.querySelector(`link[href="${CALENDLY_STYLE_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CALENDLY_STYLE_URL;
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${CALENDLY_SCRIPT_URL}"]`)) {
      const script = document.createElement('script');
      script.src = CALENDLY_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const openCalendly = () => {
    track('meeting_cta_clicked', { lens_names: lensNames }); // event 10
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: `${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1`,
      });
    } else {
      // Script not yet loaded - fall back to opening Calendly in a new tab.
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // Build the lens pills strip - Intelligence Tier (applied) + the three identified.
  const pills = [
    { num: '04', name: 'Intelligence Tier' },
    ...selectedLenses.map((l) => ({
      num: String(l.id).padStart(2, '0'),
      name: l.name,
    })),
  ];

  return (
    <section className="relative overflow-hidden bg-green-700 text-paper py-24">
      <img
        src="/logo-white.png"
        alt=""
        className="absolute -bottom-40 -right-32 w-[600px] aspect-square opacity-[0.06] pointer-events-none"
      />
      <div className="relative max-w-[1180px] mx-auto px-6">
        <div className="text-[12px] font-semibold tracking-mega uppercase text-green-200 mb-3">
          The call
        </div>
        <h2 className="font-display text-[clamp(28px,4vw,40px)] font-semibold tracking-[-0.02em] leading-[1.15] text-paper max-w-[760px] mb-5">
          The tool surfaces the tension.
          <br />
          <em className="italic font-normal text-green-200">
            The call is where we work out what to do about it.
          </em>
        </h2>
        <p className="text-[16px] text-green-100 leading-[1.7] max-w-[640px] mb-3">
          Book a thirty-minute call. We'll send a written brief beforehand -{' '}
          <strong className="text-paper font-semibold">
            applied through the Intelligence Tier lens you've just used, plus three more identified for your organisation:
          </strong>
        </p>

        <div className="flex flex-wrap gap-2 my-8">
          {pills.map((p) => (
            <span
              key={p.num + p.name}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[rgba(245,241,233,0.10)] border border-[rgba(245,241,233,0.20)] text-[13px] text-paper font-medium"
            >
              <span className="font-display text-[11px] font-bold text-green-200 tracking-[0.08em]">
                {p.num}
              </span>
              {p.name}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <button
            type="button"
            onClick={openCalendly}
            className="bg-paper text-green-800 px-7 py-4 rounded-md text-[16px] font-semibold inline-flex items-center gap-3 shadow-elev-2 hover:bg-green-50 transition-colors"
          >
            <Calendar size={18} />
            Book the call
            <ExternalLink size={14} className="opacity-60" />
          </button>
          <p className="text-[13px] text-green-200 leading-[1.55]">
            Prefer email?{' '}
            <a href="#" className="text-paper border-b border-[rgba(245,241,233,0.4)] hover:border-paper">
              Reply to the confirmation
            </a>{' '}
            we just sent and we'll find a time that way.
          </p>
        </div>
      </div>
    </section>
  );
}
